import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Eye, EyeSlash, CheckCircle } from '@phosphor-icons/react';
import IndieLawLogo from '../components/IndieLawLogo';
import { useAuth } from '../context/AuthContext';

import API_URL from '../config';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const isWelcome = searchParams.get('welcome') === '1';
  const [mode, setMode] = useState(token ? 'reset' : 'request');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [linkProblem, setLinkProblem] = useState(null); // 'expired' | 'used' | 'invalid'
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const showNewLinkFlow = (status, prefill) => {
    setLinkProblem(status);
    if (prefill) setEmail(prefill);
    setError('');
    setMode('request');
  };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/auth/reset-token-status`, { params: { token } })
      .then((res) => { if (res.data.status !== 'valid') showNewLinkFlow(res.data.status, res.data.email); })
      .catch(() => {});
  }, [token]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (error) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: newPassword, welcome: isWelcome });
      if (isWelcome && res.data.signed_in) {
        localStorage.setItem('indie_law_token', res.data.access_token);
        localStorage.setItem('indie_law_refresh', res.data.refresh_token);
        await checkAuth();
        navigate(res.data.has_protection_plan ? '/protection-plan' : '/dashboard', { replace: true });
        return;
      }
      setSuccess(true);
    } catch (error) {
      if (error.response?.status === 400) {
        const st = await axios.get(`${API_URL}/api/auth/reset-token-status`, { params: { token } }).catch(() => null);
        showNewLinkFlow(st?.data?.status || 'expired', st?.data?.email);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-white">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="mb-8">
          <IndieLawLogo variant="dark" className="h-16 w-auto" />
        </div>

        {success ? (
          <div className="text-center" data-testid="reset-success">
            <div className="w-16 h-16 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle weight="duotone" className="w-8 h-8 text-[#22C55E]" />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#141b4d' }}>
              {mode === 'request' ? 'Check Your Email' : (isWelcome ? 'You\'re All Set!' : 'Password Reset!')}
            </h2>
            <p className="mb-6" style={{ color: '#9a9488' }}>
              {mode === 'request'
                ? 'If an account exists with that email, we\'ve sent a password reset link.'
                : (isWelcome
                    ? 'Your password is set and your portal is ready. Log in to see the status of all your trademark matters.'
                    : 'Your password has been updated. You can now log in with your new password.')}
            </p>
            {mode === 'request' && (
              <p className="mb-6 text-sm" style={{ color: '#9a9488' }} data-testid="reset-fallback-note">
                Didn't get it? Check your spam folder, or email us at{' '}
                <a href="mailto:clients@indielaw.com" className="font-medium hover:underline" style={{ color: '#141b4d' }}>
                  clients@indielaw.com
                </a>{' '}and we'll help you get back in.
              </p>
            )}
            <Link to="/login" className="btn-primary inline-block" data-testid="back-to-login-btn">
              Back to Login
            </Link>
          </div>
        ) : mode === 'request' ? (
          <>
            {linkProblem ? (
              <div className="mb-6" data-testid="link-problem">
                <h2 className="text-2xl font-bold mb-2" style={{ color: '#141b4d' }} data-testid="link-problem-title">
                  {linkProblem === 'used' ? 'This link has already been used' : linkProblem === 'expired' ? 'This link has expired' : "This link isn't valid"}
                </h2>
                <p style={{ color: '#9a9488' }} data-testid="link-problem-text">
                  {linkProblem === 'used'
                    ? 'Each link works once. If you already set your password, just log in. Otherwise, we can send you a new link.'
                    : 'For your security, links stop working after a while. Enter your email and we\'ll send you a new one.'}
                </p>
              </div>
            ) : (
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2" style={{ color: '#141b4d' }}>Forgot Your Password?</h2>
                <p style={{ color: '#9a9488' }}>Enter your email and we'll send you a reset link.</p>
              </div>
            )}
            <form onSubmit={handleRequestReset} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="reset-error">{error}</div>
              )}
              <div>
                <label className="label-uppercase block mb-2">Email Address</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="input-swiss" placeholder="you@example.com" required data-testid="reset-email-input" />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-submit-btn">
                {loading ? 'Sending...' : (linkProblem ? 'Send me a new link' : 'Send Reset Link')}
              </button>
            </form>
            <p className="mt-6 text-center text-sm" style={{ color: '#9a9488' }}>
              <Link to="/login" className="font-medium hover:underline" style={{ color: '#141b4d' }}>Back to Login</Link>
            </p>
          </>
        ) : (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#141b4d' }}>{isWelcome ? 'Set Your Password' : 'Set New Password'}</h2>
              <p style={{ color: '#9a9488' }}>{isWelcome ? 'Create a password to finish setting up your portal.' : 'Enter your new password below.'}</p>
            </div>
            <form onSubmit={handleResetPassword} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="reset-error">{error}</div>
              )}
              <div>
                <label className="label-uppercase block mb-2">New Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-swiss pr-12" placeholder="At least 6 characters" required minLength={6}
                    data-testid="new-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#9a9488' }}>
                    {showPassword ? <EyeSlash weight="regular" className="w-5 h-5" /> : <Eye weight="regular" className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="label-uppercase block mb-2">Confirm Password</label>
                <input type="password" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-swiss" placeholder="Re-enter password" required
                  data-testid="confirm-password-input" />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-submit-btn">
                {loading ? (isWelcome ? 'Setting up...' : 'Resetting...') : (isWelcome ? 'Set password & see my portal' : 'Reset Password')}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
